import { useEffect, useState } from "react";
import { listAgentRegistrations } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Agent {
  id: string;
  name: string;
  displayName: string;
  status: string;
  lastHeartbeat: string | null;
  errorMessage: string | null;
  version: string;
}

function heartbeatAge(ts: string | null): string {
  if (!ts) return "Never";
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

function healthBorder(agent: Agent): string {
  if (agent.status === "ERROR") return "border-red-700 bg-red-950/20";
  if (agent.status === "INACTIVE") return "border-gray-700 bg-gray-900";
  if (!agent.lastHeartbeat) return "border-yellow-700 bg-yellow-950/20";
  const age = Date.now() - new Date(agent.lastHeartbeat).getTime();
  if (age > 300000) return "border-orange-700 bg-orange-950/20";
  return "border-green-700 bg-green-950/20";
}

export function OpsHealthPage() {
  const { token } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      void listAgentRegistrations(token).then((d: unknown) => {
        setAgents((d as { items: Agent[] }).items ?? []);
        setLoading(false);
      });
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const healthy = agents.filter((a) => a.status === "ACTIVE" && a.lastHeartbeat).length;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Agent Health</h1>
          <span className="text-sm text-gray-400">
            {healthy}/{agents.length} healthy · auto-refreshes every 30s
          </span>
        </div>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {agents.length === 0 && <p className="text-gray-400">No agents registered.</p>}
            {agents.map((agent) => (
              <div key={agent.id} className={`rounded-lg border p-4 ${healthBorder(agent)}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <span className="font-medium text-white">{agent.displayName}</span>
                    <span className="ml-2 text-xs text-gray-500">v{agent.version}</span>
                  </div>
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${
                      agent.status === "ACTIVE"
                        ? "bg-green-900/50 text-green-300"
                        : agent.status === "ERROR"
                          ? "bg-red-900/50 text-red-300"
                          : "bg-gray-800 text-gray-400"
                    }`}
                  >
                    {agent.status}
                  </span>
                </div>
                {agent.errorMessage && (
                  <p className="mb-2 text-xs text-red-400">{agent.errorMessage}</p>
                )}
                <p className="text-xs text-gray-500">
                  Heartbeat: {heartbeatAge(agent.lastHeartbeat)}
                </p>
                <p className="mt-1 font-mono text-xs text-gray-600">{agent.name}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
